import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { GradientOrbitOverlay } from '@/components/ui/gradient-orbit-border';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrandDnaChoiceStep from '@/components/brand-dna/BrandDnaChoiceStep';
import BrandDnaScanStep from '@/components/brand-dna/BrandDnaScanStep';
import BrandDnaWriteStep from '@/components/brand-dna/BrandDnaWriteStep';

type Step = 'choice' | 'write' | 'scan';

interface BrandDnaModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingDna?: string | null;
  existingSourceUrl?: string | null;
}

const BrandDnaModal = ({ isOpen, onClose, existingDna, existingSourceUrl }: BrandDnaModalProps) => {
  const { t } = useTranslation();
  const { data: brand } = useCurrentBrandQuery();
  const isEditing = !!existingDna;

  const [step, setStep] = useState<Step>('choice');
  const [isScanning, setIsScanning] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);

  const resetState = () => {
    setStep('choice');
    setIsScanning(false);
    setGeneratedText('');
    setSourceUrl(null);
  };

  const handleOpenChange = () => {
    onClose();
    resetState();
  };

  const handleGenerated = (text: string, url: string) => {
    setGeneratedText(text);
    setSourceUrl(url);
    setIsScanning(false);
    setStep('write');
  };

  const handleCancel = () => {
    if (step === 'write') {
      setStep('choice');
      return;
    }
    handleOpenChange();
  };

  const title = isEditing
    ? t('settings.brandDna.modal.editTitle')
    : t('settings.brandDna.modal.title');

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      closeOnInteractOutside={false}
      size="md"
      placement="center"
    >
      <DialogContent position="relative" overflow="visible">
        <GradientOrbitOverlay active={isScanning} borderRadius="2xl" />
        <DialogHeader position="relative" zIndex={1}>
          <DialogTitle>{title}</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody pb={6} position="relative" zIndex={1}>
          {step === 'choice' && <BrandDnaChoiceStep onContinue={setStep} />}
          {step === 'scan' && (
            <BrandDnaScanStep
              defaultUrl={brand?.websiteUrl || ''}
              onGenerated={handleGenerated}
              onWriteInstead={() => setStep('write')}
              onLoadingChange={setIsScanning}
            />
          )}
          {step === 'write' && (
            <BrandDnaWriteStep
              initialText={generatedText || existingDna || ''}
              sourceUrl={sourceUrl || existingSourceUrl}
              onCancel={handleCancel}
              onSaved={handleOpenChange}
            />
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default BrandDnaModal;
